import useIdentity from "@/features/ui/hooks/use-identity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { useEffect, useMemo, useRef } from "react";
import useListAllOrderConfirmationInvoices, {
  ListAllOrderConfirmationInvoicesRequest,
} from "@/features/invoices/order-confirmation-invoices/hooks/use-list-all-order-confirmation-invoices";
import AppExclusiveSelect from "@/features/ui/app-exclusive-select";
import {
  DenseOrderConfirmationInvoice,
  OrderConfirmationInvoice,
  OrderConfirmationInvoiceDetail,
} from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice.type";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useBiSWRResponse from "@/features/invoices/deposit-invoices/use-bi-swr-response";
import { isEmpty, uniqBy } from "lodash";
import { DenseOrderSheetSet } from "@/features/order-sheets/order-sheet.type";
import useListAllDenseOrderConfirmationInvoices from "@/features/invoices/order-confirmation-invoices/hooks/use-list-all-dense-order-confirmation-invoices";
import PriceUtils from "@/features/ui/utils/price-utils";

export interface LinkedOrderConfirmationInvoice {
  id: number;
  name: string;
  totalAmount: number;
  totalRequiredAmount: number;
  actualRemainingDepositAmount: number;
  expectedRemainingDepositAmount: number;
  orderConfirmationInvoiceDetailList: OrderConfirmationInvoiceDetail[];
  isRemitted: boolean;
}

interface AppOrderConfirmationInvoicesExclusiveSelectorProps {
  request?: ListAllOrderConfirmationInvoicesRequest;
  // value: LinkedOrderConfirmationInvoice[] | LinkedOrderConfirmationInvoice;
  id?: number | number[];
  onSelect: (value: DenseOrderConfirmationInvoice[]) => void;
  isDisabled?: boolean;
  nameFunc?: (ocInvoice: DenseOrderConfirmationInvoice) => string;
  // 선택지에서 거를 조건 — 이미 고른 값은 거르지 않는다
  optionFilter?: (ocInvoice: DenseOrderConfirmationInvoice) => boolean;
  // 선택지가 뜨고 아직 고른 게 없으면 첫 번째를 자동으로 고른다(후보 목록 기준으로 한 번만 — 담당자가 지우면 다시 고르지 않는다)
  autoSelectFirst?: boolean;
}

export default function AppOrderConfirmationInvoicesExclusiveSelector({
  request,
  // value,
  id,
  onSelect,
  isDisabled,
  nameFunc,
  optionFilter,
  autoSelectFirst = false,
}: AppOrderConfirmationInvoicesExclusiveSelectorProps) {
  let isMultiple = Array.isArray(id) || id === undefined;
  const identity = useIdentity();
  const { error: showError } = useAppToasts();
  const { tTitle, tCurrencyString } = useI18nHelper();

  const key1 = useMemo(() => {
    if (identity?.company && request && !isEmpty(request)) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        // 선택지 이름(OC id·제목·디파짓)은 OC 응답만으로 만든다 — 오더·라인시트 조회 생략
        request: { ...request, skipOrderSheetSets: true },
      };
    }
  }, [identity, request]);

  const key2 = useMemo(() => {
    if (identity?.company && id) {
      const id__in = Array.isArray(id) ? id : [id];
      if (id__in.length > 0) {
        return {
          parameter: {
            by: identity.company.type,
            companyId: identity.company.id,
          },
          request: {
            id__in: Array.isArray(id) ? id : [id],
            skipOrderSheetSets: true,
          },
        };
      }
    }
  }, [id, identity]);

  const response1 = useListAllDenseOrderConfirmationInvoices(
    key1?.parameter,
    key1?.request
  );

  const response2 = useListAllDenseOrderConfirmationInvoices(
    key2?.parameter,
    key2?.request
  );

  const {
    data: resources,
    isLoading: isListResourcesLoading,
    error: isListResourcesError,
  } = useBiSWRResponse(response1, response2, (data1, data2) => {
    return uniqBy([...(data1 || []), ...(data2 || [])], (i) => i.id);
  });


  const value = useMemo(() => {
    if (resources && id) {
      const ids = Array.isArray(id) ? id : [id];
      return ids
        .map((id) => resources.find((i) => i.id === id))
        .filter(
          (resource): resource is DenseOrderConfirmationInvoice =>
            resource !== undefined
        );
    }
    return [];
  }, [resources, id]);

  const options = useMemo(
    () => (resources || []).filter((ocInvoice) => !optionFilter || optionFilter(ocInvoice)),
    [resources, optionFilter]
  );

  // 자동 선택은 후보 목록(요청 조건)마다 한 번만
  const autoSelectedKey = useRef<string>();
  const optionsKey = key1 ? JSON.stringify(key1.request) : undefined;
  useEffect(() => {
    if (
      !autoSelectFirst ||
      isDisabled ||
      isListResourcesLoading ||
      !optionsKey ||
      autoSelectedKey.current === optionsKey ||
      options.length === 0
    ) {
      return;
    }
    autoSelectedKey.current = optionsKey;
    if (value.length === 0) {
      onSelect([options[0]]);
    }
  }, [autoSelectFirst, isDisabled, isListResourcesLoading, optionsKey, options, value, onSelect]);

  return (
    <AppExclusiveSelect<DenseOrderConfirmationInvoice>
      name={"order_confirmation_invoice"}
      values={value}
      options={options}
      optionFunc={(ocInvoice) => {
        let name = "";
        if (nameFunc) {
          name = nameFunc(ocInvoice);
        } else {
          name = `${ocInvoice.id} / ${ocInvoice.name} `;
          name += `(${tTitle("deposit")} - `;
          name += `${tTitle("total")} : ${tCurrencyString(
            ocInvoice.totalRequiredAmount
          )}`;

          if (ocInvoice.isRemittedToBoutique) {
            name += ", ";
            name += `${tTitle("used")} : `;
            name += tCurrencyString(
              PriceUtils.placeholder(
                PriceUtils.minus(
                  [
                    ocInvoice.totalRequiredAmount,
                    ocInvoice.expectedRemainingDepositAmount,
                  ],
                  "EUR"
                ),
                "EUR"
              )
            );
            name += ` `;
            name += `${tTitle("remaining")} : ${tCurrencyString(
              ocInvoice.expectedRemainingDepositAmount
            )})`;
          }

          name += ")";
        }

        return {
          value: ocInvoice,
          name: name,
        };
      }}
      onSelect={onSelect}
      isDisabled={isDisabled}
      isLoading={isListResourcesLoading}
      isMultiple={isMultiple}
    />
  );
}
